import { useEffect, useRef, useState } from "react";
import { Search, Loader2, Check, AlertCircle } from "lucide-react";

import { Button } from "@/core/components/ui/button";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import { NoTranslate } from "@/core/components/ui/no-translate";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { useResolveWalletByPhone } from "@/features/wallet/api/use-resolve-wallet-by-phone";
import type { WalletResolveByPhoneResponse } from "@/features/wallet/api/dto";

interface Props {
  /** Appelé quand un portefeuille est résolu — reçoit le walletNumber à injecter. */
  onResolved: (resolved: WalletResolveByPhoneResponse) => void;
}

/**
 * Helper « Rechercher par téléphone » (guide v2.4 §6.3,
 * `POST /Wallets/resolve-by-phone`). Debounce 500 ms après la saisie, puis
 * appel idempotent (sans effet métier). Affiche le portefeuille résolu
 * (walletNumber + nom d'affichage) et appelle `onResolved` quand l'utilisateur
 * valide le choix.
 */
export function WalletLookupByPhone({ onResolved }: Props) {
  const { t } = useTypedTranslation();
  const tx = (key: string) => t(key as never, {} as never) as string;

  const [phone, setPhone] = useState("");
  const [debouncedPhone, setDebouncedPhone] = useState("");
  const resolve = useResolveWalletByPhone();
  const [resolved, setResolved] = useState<WalletResolveByPhoneResponse | null>(
    null
  );
  const [notFound, setNotFound] = useState(false);
  const lastQueryRef = useRef<string>("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedPhone(phone.trim()), 500);
    return () => clearTimeout(timer);
  }, [phone]);

  useEffect(() => {
    if (!debouncedPhone || !debouncedPhone.startsWith("+")) {
      setResolved(null);
      setNotFound(false);
      return;
    }
    if (lastQueryRef.current === debouncedPhone) return;
    lastQueryRef.current = debouncedPhone;
    resolve.mutate(
      { phone: debouncedPhone },
      {
        onSuccess: (data) => {
          setResolved(data);
          setNotFound(false);
        },
        onError: () => {
          setResolved(null);
          setNotFound(true);
        },
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPhone]);

  return (
    <div className="flex flex-col gap-2 rounded-md border border-dashed border-border/70 p-3 sm:col-span-2">
      <Label htmlFor="lookup-phone" className="flex items-center gap-2">
        <Search className="h-4 w-4 text-muted-foreground" />
        {tx("wallet.beneficiaries.lookup.title")}
      </Label>
      <p className="text-xs text-muted-foreground">
        {tx("wallet.beneficiaries.lookup.description")}
      </p>
      <div className="flex items-center gap-2">
        <Input
          id="lookup-phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+224620000000"
          inputMode="tel"
          autoComplete="off"
        />
        {resolve.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : null}
      </div>
      {resolved ? (
        <div className="flex items-center justify-between gap-2 rounded-md bg-success-muted px-2 py-2 text-sm">
          <div className="flex min-w-0 flex-col">
            <NoTranslate as="span" className="font-medium">
              {resolved.displayName ?? resolved.walletNumber}
            </NoTranslate>
            <NoTranslate as="span" className="font-mono text-xs text-muted-foreground">
              {resolved.walletNumber}
            </NoTranslate>
          </div>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => onResolved(resolved)}
          >
            <Check className="mr-1 h-3.5 w-3.5" />
            {tx("wallet.beneficiaries.lookup.use")}
          </Button>
        </div>
      ) : null}
      {notFound ? (
        <p className="flex items-center gap-2 text-xs text-destructive">
          <AlertCircle className="h-3.5 w-3.5" />
          {tx("wallet.beneficiaries.lookup.notFound")}
        </p>
      ) : null}
    </div>
  );
}
